<template>
  <top-navigation
    v-if="eventData"
    :title="$t('pages.event.title')"
  ></top-navigation>
  <div v-if="eventData" class="container mx-auto min-h-screen px-2 pb-24 pt-20">
    <!-- Event Card -->
    <div
      class="mb-6 overflow-hidden rounded-lg border-2 border-black-dark bg-white p-4 shadow-blue-hard dark:bg-kekkle-dark-light"
    >
      <!-- Header -->
      <div class="mb-3">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-slate-100">
          {{ eventData.name }}
        </h1>
        <div
          class="mt-2 flex items-center gap-3 text-sm text-gray-600 dark:text-slate-400"
        >
          <span class="flex items-center gap-1">
            <span>📅</span>
            <span>{{ from }}</span>
          </span>
          <span class="flex items-center gap-1">
            <span>→</span>
            <span>{{ till }}</span>
          </span>
        </div>
        <p
          v-if="createdBy"
          class="mt-1 text-sm text-gray-500 dark:text-slate-500"
        >
          <span>✨</span> {{ createdBy }}
        </p>
      </div>

      <!-- Description -->
      <div
        v-if="eventData.description"
        class="mb-4 rounded-lg bg-gray-50 p-3 dark:bg-kekkle-dark"
      >
        <p class="mb-1 text-sm font-medium text-gray-500 dark:text-slate-400">
          {{ $t("global.description") }}
        </p>
        <p class="text-sm text-gray-700 dark:text-slate-300">
          {{ eventData.description }}
        </p>
      </div>

      <!-- Attendance Section -->
      <div class="space-y-3">
        <!-- Going -->
        <div>
          <div class="mb-2 flex items-center gap-2">
            <div
              class="flex h-6 w-6 items-center justify-center rounded-lg bg-green-400 text-xs font-bold"
            >
              ✓
            </div>
            <span class="text-sm font-medium text-gray-700 dark:text-slate-300">
              {{ $t("components.agenda_items.present") }}
              <span class="ml-1 text-xs text-gray-500 dark:text-slate-400">
                ({{ eventData.available?.length || 0 }})
              </span>
            </span>
          </div>
          <div
            v-if="eventData.available && eventData.available.length > 0"
            class="flex flex-wrap gap-3"
          >
            <div
              v-for="uuid in eventData.available"
              :key="uuid"
              class="flex flex-col items-center"
            >
              <small-user-thumb color="green" :uuid="uuid" />
              <p
                class="mt-1 max-w-[64px] truncate text-xs font-medium dark:text-slate-300"
              >
                {{ groupData.members[uuid]?.name || "" }}
              </p>
            </div>
          </div>
          <p v-else class="text-xs text-gray-400 dark:text-slate-500">
            {{ $t("components.agenda_items.nobody_present") }}
          </p>
        </div>

        <!-- Maybe -->
        <div>
          <div class="mb-2 flex items-center gap-2">
            <div
              class="flex h-6 w-6 items-center justify-center rounded-lg bg-orange text-xs font-bold"
            >
              ?
            </div>
            <span class="text-sm font-medium text-gray-700 dark:text-slate-300">
              {{ $t("components.agenda_items.maybe") }}
              <span class="ml-1 text-xs text-gray-500 dark:text-slate-400">
                ({{ eventData.maybe_available?.length || 0 }})
              </span>
            </span>
          </div>
          <div
            v-if="
              eventData.maybe_available && eventData.maybe_available.length > 0
            "
            class="flex flex-wrap gap-3"
          >
            <div
              v-for="uuid in eventData.maybe_available"
              :key="uuid"
              class="flex flex-col items-center"
            >
              <small-user-thumb color="orange" :uuid="uuid" />
              <p
                class="mt-1 max-w-[64px] truncate text-xs font-medium dark:text-slate-300"
              >
                {{ groupData.members[uuid]?.name || "" }}
              </p>
            </div>
          </div>
          <p v-else class="text-xs text-gray-400 dark:text-slate-500">
            {{ $t("components.agenda_items.nobody_maybe") }}
          </p>
        </div>

        <!-- Not Going -->
        <div>
          <div class="mb-2 flex items-center gap-2">
            <div
              class="flex h-6 w-6 items-center justify-center rounded-lg bg-red-400 text-xs font-bold"
            >
              ✕
            </div>
            <span class="text-sm font-medium text-gray-700 dark:text-slate-300">
              {{ $t("components.agenda_items.absent") }}
              <span class="ml-1 text-xs text-gray-500 dark:text-slate-400">
                ({{ eventData.non_available?.length || 0 }})
              </span>
            </span>
          </div>
          <div
            v-if="eventData.non_available && eventData.non_available.length > 0"
            class="flex flex-wrap gap-3"
          >
            <div
              v-for="uuid in eventData.non_available"
              :key="uuid"
              class="flex flex-col items-center"
            >
              <small-user-thumb color="red" :uuid="uuid" />
              <p
                class="mt-1 max-w-[64px] truncate text-xs font-medium dark:text-slate-300"
              >
                {{ groupData.members[uuid]?.name || "" }}
              </p>
            </div>
          </div>
          <p v-else class="text-xs text-gray-400 dark:text-slate-500">
            {{ $t("components.agenda_items.nobody_absent") }}
          </p>
        </div>
      </div>
    </div>

    <!-- Comments Section -->
    <event-comments
      :event-id="eventData._id"
      :group-id="groupId"
    ></event-comments>
  </div>
</template>
<script>
import TopNavigation from "../components/TopNavigation.vue";
import SmallUserThumb from "../components/SmallUserThumb.vue";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useEventsStore } from "@/stores/modules/events/events";
import { ImageSizes } from "@/enums/images-sizes";
import { dateAgo } from "@/helpers/dates/date-ago";
import EventComments from "../components/EventComments.vue";
import { nextTick } from "vue";

export default {
  name: "ViewEventView",
  components: { SmallUserThumb, TopNavigation, EventComments },
  props: {
    groupId: {
      required: true,
    },
    eventId: {
      required: true,
    },
  },
  setup() {
    const groupsStore = useGroupsStore();
    const authStore = useAuthStore();
    const eventsStore = useEventsStore();
    return { groupsStore, authStore, eventsStore };
  },
  data() {
    return {
      SD: ImageSizes.SD,
    };
  },
  computed: {
    eventData() {
      return this.eventsStore.selectedCalendarEventDoc;
    },
    from() {
      return dateAgo(this.eventData.begin_timestamp);
    },
    till() {
      return dateAgo(this.eventData.end_timestamp);
    },
    groupData() {
      return this.groupsStore.groupData;
    },
    createdBy() {
      if (!this.eventData?.created_by) return "";
      return this.groupData?.members?.[this.eventData.created_by]?.name ?? "";
    },
  },
  async created() {
    this.eventsStore.setSelectedCalendarEventId(this.$props.eventId);
    if (!this.eventsStore.hasSelectedCalendarEvent) {
      await this.$router.push({ name: "Agenda" });
    }
  },
};
</script>
